import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildFeedbackMailto } from '@coparent/shared/src/utils/feedback';
import './LandingPage.css';

const SITE_URL = 'https://letscoparent.us';
const META_TITLE = 'coparent — Shared Custody Calendar & Expense Splitting for Two Homes';
const META_DESCRIPTION = "Keep your kids' calendar, expenses, and custody schedule in one calm shared space. Built for amicable coparents. Free on iPhone, iPad, and the web.";
const OG_IMAGE = `${SITE_URL}/logo512.png`;

const landingFeedbackHref = buildFeedbackMailto({
  platform: 'Web (landing)',
  version: process.env.REACT_APP_VERSION ?? 'web',
});

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
);

const ChevronIcon = () => (
  <svg className="lp-faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
);

const SharedRootsLogo = ({ width = 28, height = 30, variant = 'default' }) => {
  const colors = {
    default: { trunk: '#1B6B4A', child: '#3BA676', trunkWidth: '7', branchWidth: '6', sunbeamOpacity: '0.85', blushOpacity: '0.85' },
    white: { trunk: 'rgba(255,255,255,0.6)', child: 'white', trunkWidth: '8', branchWidth: '7', sunbeamOpacity: '0.85', blushOpacity: '0.85', childOpacity: '0.95' },
    muted: { trunk: 'rgba(255,255,255,0.5)', child: 'white', trunkWidth: '7', branchWidth: '6', sunbeamOpacity: '0.7', blushOpacity: '0.7', childOpacity: '0.6' },
    faded: { trunk: '#1B6B4A', child: '#3BA676', trunkWidth: '7', branchWidth: '6', sunbeamOpacity: '1', blushOpacity: '1' },
  };
  const c = colors[variant] || colors.default;
  return (
    <svg width={width} height={height} viewBox="0 0 160 160" fill="none">
      <path d="M80 140 L80 95" stroke={c.trunk} strokeWidth={c.trunkWidth} strokeLinecap="round"/>
      <path d="M80 95 Q65 80, 50 65" stroke={c.trunk} strokeWidth={c.branchWidth} strokeLinecap="round" fill="none"/>
      <path d="M80 95 Q95 80, 110 65" stroke={c.trunk} strokeWidth={c.branchWidth} strokeLinecap="round" fill="none"/>
      <circle cx="44" cy="50" r="25" fill="#F5C842" opacity={c.sunbeamOpacity}/>
      <circle cx="116" cy="50" r="25" fill="#F2886D" opacity={c.blushOpacity}/>
      <circle cx="80" cy="92" r="10" fill={c.child} opacity={c.childOpacity || '1'}/>
    </svg>
  );
};

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`lp-faq-item${isOpen ? ' open' : ''}`}>
      <button className="lp-faq-question" onClick={() => setIsOpen(!isOpen)}>
        {question}
        <ChevronIcon />
      </button>
      <div className="lp-faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const landingRef = useRef(null);

  // Scroll-reveal observer
  useEffect(() => {
    const reveals = landingRef.current?.querySelectorAll('.reveal');
    if (!reveals) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Nav scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((id) => {
    setMobileNavOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const goToRegister = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  const goToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  const faqData = [
    { question: 'Do both parents need to sign up?', answer: 'Yes, each parent creates their own free account. One parent sets up the family and sends an invite code to the other. It takes about a minute for the second parent to join.' },
    { question: "Is my family's information private?", answer: 'Absolutely. Your data is encrypted and only visible to the two parents in your family. We never share, sell, or use your data for advertising. Privacy is fundamental to what we do.' },
    { question: 'What custody schedules are supported?', answer: 'We support all common patterns \u2014 alternating weeks, 2-2-3, 3-4-4-3, 5-2-2-5, and fully custom schedules. You can also make one-off changes with our day swap feature.' },
    { question: 'Can I use coparent if we\'re still figuring things out?', answer: 'Of course. coparent works at every stage \u2014 whether you have a formal custody agreement or you\'re still working things out. The schedule is flexible and easy to change as your situation evolves.' },
    { question: 'Is there a mobile app?', answer: 'Yes \u2014 coparent is on the App Store for iPhone and iPad, and on Google Play for Android. You can also use coparent in any browser if you prefer. The same account works everywhere.' },
    { question: 'How does expense splitting work?', answer: 'Either parent can log an expense with a category, amount, and split ratio (50/50 by default). The running balance tracks who owes what, so you can settle up on your own terms.' },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'coparent',
    url: SITE_URL,
    logo: OG_IMAGE,
    description: META_DESCRIPTION,
    sameAs: [
      'https://apps.apple.com/us/app/coparent-co-parenting-hub/id6761960413',
      'https://play.google.com/store/apps/details?id=com.letscoparent.app',
    ],
  };

  const softwareAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'coparent',
    operatingSystem: 'iOS, Android, Web',
    applicationCategory: 'LifestyleApplication',
    description: META_DESCRIPTION,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <div className="landing-page" ref={landingRef}>
      <Helmet>
        <title>{META_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href={SITE_URL + '/'} />
        <meta property="og:title" content={META_TITLE} />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL + '/'} />
        <meta property="og:site_name" content="coparent" />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={META_TITLE} />
        <meta name="twitter:description" content={META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* ── NAVIGATION ── */}
      <nav className={`lp-nav${navScrolled ? ' scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <div className="lp-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <SharedRootsLogo width={38} height={40} />
            <span className="lp-nav-wordmark">coparent</span>
          </div>
          <ul className={`lp-nav-links${mobileNavOpen ? ' mobile-open' : ''}`}>
            <li><button className="lp-nav-link-btn" onClick={() => scrollToSection('features')}>Features</button></li>
            <li><button className="lp-nav-link-btn" onClick={() => scrollToSection('how-it-works')}>How It Works</button></li>
            <li><button className="lp-nav-link-btn" onClick={() => scrollToSection('faq')}>FAQ</button></li>
            <li><button className="lp-nav-link-btn" onClick={() => navigate('/blog')}>Blog</button></li>
            <li><button className="lp-nav-login" onClick={goToLogin}>Log In</button></li>
            <li><button className="lp-nav-cta" onClick={goToRegister}>Get Started</button></li>
          </ul>
          <button className="lp-nav-mobile-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)} aria-label="Toggle menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero-bg-shape lp-hero-bg-shape-1"></div>
        <div className="lp-hero-bg-shape lp-hero-bg-shape-2"></div>
        <div className="lp-hero-bg-shape lp-hero-bg-shape-3"></div>
        <div className="lp-container">
          <div className="lp-hero-content">
            <div className="lp-hero-main">
              <a
                className="lp-hero-badge"
                href="https://play.google.com/store/apps/details?id=com.letscoparent.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="lp-hero-badge-dot"></span>
                Now on Google Play
              </a>
              <h1 className="lp-hero-h1">Two homes.<br /><em>One plan.</em></h1>
              <p className="lp-hero-sub">coparent keeps your kids' calendar, expenses, and custody schedule in one shared space &mdash; so both parents always know what's next.</p>
              <div className="lp-hero-actions">
                <button className="lp-btn-primary" onClick={goToRegister}>
                  Get Started Free
                  <svg className="lp-btn-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>
                <button className="lp-btn-secondary" onClick={() => scrollToSection('how-it-works')}>
                  See How It Works
                </button>
              </div>
            </div>
            <div className="lp-hero-appstore-slot">
              <a
                className="lp-btn-appstore"
                href="https://apps.apple.com/us/app/coparent-co-parenting-hub/id6761960413"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download coparent on the App Store"
              >
                <svg className="lp-btn-appstore-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.564 12.704c-.023-2.46 2.007-3.648 2.1-3.703-1.147-1.674-2.927-1.904-3.56-1.929-1.515-.153-2.956.893-3.725.893-.77 0-1.953-.871-3.212-.846-1.652.024-3.175.961-4.024 2.442-1.716 2.972-.44 7.364 1.232 9.777.819 1.18 1.79 2.501 3.062 2.454 1.23-.05 1.693-.793 3.179-.793 1.485 0 1.901.793 3.2.767 1.322-.024 2.16-1.202 2.968-2.388.936-1.37 1.32-2.714 1.343-2.783-.03-.012-2.58-.988-2.563-3.891zM15.13 5.432c.678-.823 1.137-1.965 1.012-3.104-.978.04-2.164.652-2.866 1.473-.629.728-1.18 1.893-1.031 3.012 1.093.084 2.207-.554 2.885-1.381z"/></svg>
                <span className="lp-btn-appstore-text">
                  <span className="lp-btn-appstore-line1">Download on the</span>
                  <span className="lp-btn-appstore-line2">App Store</span>
                </span>
              </a>
              <a
                className="lp-btn-appstore"
                href="https://play.google.com/store/apps/details?id=com.letscoparent.app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get coparent on Google Play"
              >
                <svg className="lp-btn-appstore-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.185 12l2.513-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/></svg>
                <span className="lp-btn-appstore-text">
                  <span className="lp-btn-appstore-line1">Get it on</span>
                  <span className="lp-btn-appstore-line2">Google Play</span>
                </span>
              </a>
            </div>
          </div>
          <div className="lp-hero-visual" role="img" aria-label="coparent mobile app showing a shared custody calendar, today's events, and the running expense balance for both parents.">
            {/* Floating card: Schedule */}
            <div className="lp-hero-float lp-hero-float-1">
              <div className="lp-hero-float-icon" style={{ background: 'var(--sunbeam-20)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B8920A" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></svg>
              </div>
              <div className="lp-hero-float-label">This week</div>
              <div className="lp-hero-float-value">Mom's days</div>
            </div>
            {/* Floating card: Balance */}
            <div className="lp-hero-float lp-hero-float-2">
              <div className="lp-hero-float-icon" style={{ background: 'var(--canopy-10)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1B6B4A" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
              </div>
              <div className="lp-hero-float-label">Balance</div>
              <div className="lp-hero-float-value">Settled up</div>
            </div>

            <div className="lp-hero-phone">
              <div className="lp-hero-phone-screen">
                <div className="lp-phone-header">
                  <div className="lp-phone-header-bar">
                    <span>9:41</span>
                    <span>5G &nbsp; 100%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <SharedRootsLogo width={24} height={26} variant="white" />
                    <div>
                      <div className="lp-phone-header-greeting">Good morning</div>
                      <div className="lp-phone-header-sub">Wednesday &middot; Dad's week</div>
                    </div>
                  </div>
                </div>
                <div className="lp-phone-body">
                  <div className="lp-phone-card">
                    <div className="lp-phone-card-head">
                      <span className="lp-phone-card-title">Today</span>
                      <span className="lp-phone-badge lp-phone-badge-today">2 Events</span>
                    </div>
                    <div className="lp-phone-event">
                      <div className="lp-phone-event-dot" style={{ background: 'var(--sunbeam)' }}></div>
                      <div>
                        <div className="lp-phone-event-name">Soccer Practice</div>
                        <div className="lp-phone-event-time">3:30 PM &middot; Riverside Park</div>
                      </div>
                    </div>
                    <div className="lp-phone-event">
                      <div className="lp-phone-event-dot" style={{ background: 'var(--blush)' }}></div>
                      <div>
                        <div className="lp-phone-event-name">Piano Lesson</div>
                        <div className="lp-phone-event-time">5:00 PM &middot; Ms. Chen's</div>
                      </div>
                    </div>
                  </div>
                  <div className="lp-phone-card">
                    <div className="lp-phone-card-head">
                      <span className="lp-phone-card-title">Expenses</span>
                      <span className="lp-phone-badge lp-phone-badge-new">1 New</span>
                    </div>
                    <div className="lp-phone-expense-row">
                      <span className="lp-phone-expense-label">Soccer cleats</span>
                      <span className="lp-phone-expense-amount">$45.00</span>
                    </div>
                    <div className="lp-phone-expense-row">
                      <span className="lp-phone-expense-label">School photos</span>
                      <span className="lp-phone-expense-amount">$28.00</span>
                    </div>
                  </div>
                  <div className="lp-phone-card">
                    <div className="lp-phone-card-head">
                      <span className="lp-phone-card-title">Custody</span>
                    </div>
                    <div className="lp-phone-custody-bar">
                      <div className="lp-phone-custody-day" style={{ background: 'var(--blush)' }}></div>
                      <div className="lp-phone-custody-day" style={{ background: 'var(--blush)' }}></div>
                      <div className="lp-phone-custody-day today" style={{ background: 'var(--sunbeam)' }}></div>
                      <div className="lp-phone-custody-day" style={{ background: 'var(--sunbeam)' }}></div>
                      <div className="lp-phone-custody-day" style={{ background: 'var(--sunbeam)' }}></div>
                      <div className="lp-phone-custody-day" style={{ background: 'var(--blush)' }}></div>
                      <div className="lp-phone-custody-day" style={{ background: 'var(--blush)' }}></div>
                    </div>
                    <div className="lp-phone-custody-labels">
                      <span>Mon</span><span>Tue</span><span className="today-label">Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                    </div>
                  </div>
                </div>
                <div className="lp-phone-nav-bar">
                  <div className="lp-phone-nav-item active">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9"/><path d="M9 21V9h6v12"/></svg>
                    Home
                  </div>
                  <div className="lp-phone-nav-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    Calendar
                  </div>
                  <div className="lp-phone-nav-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                    Expenses
                  </div>
                  <div className="lp-phone-nav-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                    Messages
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="lp-features lp-section-padding" id="features">
        <div className="lp-container">
          <div className="lp-features-header reveal">
            <div className="lp-section-label">Features</div>
            <div className="lp-section-title">Everything both parents need,<br />in one calm place</div>
            <p className="lp-section-desc">No more scattered texts, missed pickups, or "I thought it was your turn." coparent brings clarity to every part of shared parenting.</p>
          </div>
          <div className="lp-features-grid">
            <div className="lp-feature-card reveal reveal-delay-1">
              <div className="lp-feature-icon lp-feature-icon-calendar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <h3 className="lp-feature-title">Shared Custody Calendar</h3>
              <p className="lp-feature-desc">Color-coded days make it clear who has the kids. Both parents see the same schedule, always up to date.</p>
            </div>
            <div className="lp-feature-card reveal reveal-delay-2">
              <div className="lp-feature-icon lp-feature-icon-expense">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
              </div>
              <h3 className="lp-feature-title">Expense Splitting</h3>
              <p className="lp-feature-desc">Track shared costs, split them fairly, and settle up without awkward conversations. Every dollar accounted for.</p>
            </div>
            <div className="lp-feature-card reveal reveal-delay-3">
              <div className="lp-feature-icon lp-feature-icon-activity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
              </div>
              <h3 className="lp-feature-title">Activity Management</h3>
              <p className="lp-feature-desc">Soccer, piano, tutoring &mdash; all your kids' activities in one place with schedules, locations, and costs attached.</p>
            </div>
            <div className="lp-feature-card reveal reveal-delay-1">
              <div className="lp-feature-icon lp-feature-icon-message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              </div>
              <h3 className="lp-feature-title">In-App Messaging</h3>
              <p className="lp-feature-desc">Keep parenting conversations organized and in context. No more scrolling through texts to find that one detail.</p>
            </div>
            <div className="lp-feature-card reveal reveal-delay-2">
              <div className="lp-feature-icon lp-feature-icon-swap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 3l4 4-4 4"/><path d="M20 7H4"/><path d="M8 21l-4-4 4-4"/><path d="M4 17h16"/></svg>
              </div>
              <h3 className="lp-feature-title">Day Swap Requests</h3>
              <p className="lp-feature-desc">Need to switch a day? Propose a swap, and your co-parent confirms with one tap. Simple, documented, fair.</p>
            </div>
            <div className="lp-feature-card reveal reveal-delay-3">
              <div className="lp-feature-icon lp-feature-icon-sync">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21.5 2v6h-6"/><path d="M2.5 22v-6h6"/><path d="M2.5 16A10 10 0 0118 4.7"/><path d="M21.5 8A10 10 0 016 19.3"/></svg>
              </div>
              <h3 className="lp-feature-title">Real-Time Sync</h3>
              <p className="lp-feature-desc">Changes appear instantly for both parents. Add an event, update a schedule, log an expense &mdash; it's synced in real time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-section-padding" id="how-it-works">
        <div className="lp-container">
          <div className="lp-how-header reveal">
            <div className="lp-section-label">How It Works</div>
            <div className="lp-section-title">Up and running in 3 minutes</div>
            <p className="lp-section-desc">No complicated setup. No learning curve. Just three quick steps and you're coordinating like pros.</p>
          </div>
          <div className="lp-steps">
            <div className="lp-step reveal reveal-delay-1">
              <div className="lp-step-number">1</div>
              <h3 className="lp-step-title">Create Your Family</h3>
              <p className="lp-step-desc">Sign up and add your kids. It takes about 60 seconds to set up your shared family space.</p>
            </div>
            <div className="lp-step reveal reveal-delay-2">
              <div className="lp-step-number">2</div>
              <h3 className="lp-step-title">Set Your Schedule</h3>
              <p className="lp-step-desc">Pick a custody pattern &mdash; alternating weeks, 2-2-3, or custom. We'll build your calendar automatically.</p>
            </div>
            <div className="lp-step reveal reveal-delay-3">
              <div className="lp-step-number">3</div>
              <h3 className="lp-step-title">Invite Your Co-Parent</h3>
              <p className="lp-step-desc">Share a simple invite code. Once they join, you're both looking at the same plan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SHOWCASE ── */}
      <section className="lp-showcase lp-section-padding">
        <div className="lp-container">
          <div className="lp-section-label reveal">Built for Real Life</div>
          <div className="lp-section-title reveal reveal-delay-1">The calm command center<br />for modern coparenting</div>
          <p className="lp-section-desc reveal reveal-delay-2">Not a legal tool. Not another messaging app. A purpose-built platform where custody, activities, and expenses live in one shared view.</p>
          <div className="lp-showcase-grid">
            <div className="lp-showcase-card reveal reveal-delay-1">
              <div className="lp-showcase-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div className="lp-showcase-card-title">Private &amp; Secure</div>
              <div className="lp-showcase-card-desc">Your family data is encrypted and only visible to the parents in your family. We never share or sell data.</div>
            </div>
            <div className="lp-showcase-card reveal reveal-delay-2">
              <div className="lp-showcase-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              </div>
              <div className="lp-showcase-card-title">Any Device</div>
              <div className="lp-showcase-card-desc">Works on phones, tablets, and computers. Your plan goes wherever you go.</div>
            </div>
            <div className="lp-showcase-card reveal reveal-delay-3">
              <div className="lp-showcase-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              </div>
              <div className="lp-showcase-card-title">Built for Two</div>
              <div className="lp-showcase-card-desc">Every feature is designed for two equal parents. No "admin" or "primary" &mdash; just partners in parenting.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section-padding" id="faq">
        <div className="lp-container">
          <div className="lp-faq-header reveal">
            <div className="lp-section-label">FAQ</div>
            <div className="lp-section-title">Common questions</div>
            <p className="lp-section-desc">If you don't see your answer here, reach out &mdash; we're happy to help.</p>
          </div>
          <div className="lp-faq-list">
            {faqData.map((item, i) => (
              <div className="reveal" key={i}>
                <FaqItem question={item.question} answer={item.answer} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="lp-final-cta lp-section-padding" id="get-started">
        <div className="lp-container">
          <div className="lp-final-cta-card reveal">
            <svg width="64" height="68" viewBox="0 0 160 160" fill="none" style={{ margin: '0 auto 32px', display: 'block', opacity: 0.15 }}>
              <path d="M80 140 L80 95" stroke="#1B6B4A" strokeWidth="7" strokeLinecap="round"/>
              <path d="M80 95 Q65 80, 50 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none"/>
              <path d="M80 95 Q95 80, 110 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none"/>
              <circle cx="44" cy="50" r="25" fill="#F5C842"/>
              <circle cx="116" cy="50" r="25" fill="#F2886D"/>
              <circle cx="80" cy="92" r="10" fill="#3BA676"/>
            </svg>
            <div className="lp-section-title">Your kids deserve parents<br />who are in sync</div>
            <p className="lp-section-desc">Replace confusion with clarity. It's free to start, and takes about 3 minutes to set up.</p>
            <div className="lp-hero-actions" style={{ justifyContent: 'center', opacity: 1, animation: 'none' }}>
              <button className="lp-btn-primary" onClick={goToRegister}>
                Get Started Free
                <svg className="lp-btn-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div>
              <div className="lp-nav-logo" style={{ display: 'inline-flex', marginBottom: '4px', cursor: 'default' }}>
                <SharedRootsLogo width={24} height={26} variant="muted" />
                <span style={{ fontFamily: "var(--font-display)", fontSize: '20px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', letterSpacing: '-0.5px' }}>coparent</span>
              </div>
              <p className="lp-footer-brand-desc">The calm command center for modern coparenting. Two homes, one plan.</p>
              <div className="lp-footer-stores">
              <a
                className="lp-btn-appstore lp-btn-appstore-footer"
                href="https://apps.apple.com/us/app/coparent-co-parenting-hub/id6761960413"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download coparent on the App Store"
              >
                <svg className="lp-btn-appstore-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.564 12.704c-.023-2.46 2.007-3.648 2.1-3.703-1.147-1.674-2.927-1.904-3.56-1.929-1.515-.153-2.956.893-3.725.893-.77 0-1.953-.871-3.212-.846-1.652.024-3.175.961-4.024 2.442-1.716 2.972-.44 7.364 1.232 9.777.819 1.18 1.79 2.501 3.062 2.454 1.23-.05 1.693-.793 3.179-.793 1.485 0 1.901.793 3.2.767 1.322-.024 2.16-1.202 2.968-2.388.936-1.37 1.32-2.714 1.343-2.783-.03-.012-2.58-.988-2.563-3.891zM15.13 5.432c.678-.823 1.137-1.965 1.012-3.104-.978.04-2.164.652-2.866 1.473-.629.728-1.18 1.893-1.031 3.012 1.093.084 2.207-.554 2.885-1.381z"/></svg>
                <span className="lp-btn-appstore-text">
                  <span className="lp-btn-appstore-line1">Download on the</span>
                  <span className="lp-btn-appstore-line2">App Store</span>
                </span>
              </a>
              <a
                className="lp-btn-appstore lp-btn-appstore-footer"
                href="https://play.google.com/store/apps/details?id=com.letscoparent.app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get coparent on Google Play"
              >
                <svg className="lp-btn-appstore-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.185 12l2.513-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/></svg>
                <span className="lp-btn-appstore-text">
                  <span className="lp-btn-appstore-line1">Get it on</span>
                  <span className="lp-btn-appstore-line2">Google Play</span>
                </span>
              </a>
              </div>
            </div>
            <div>
              <div className="lp-footer-col-title">Product</div>
              <ul className="lp-footer-links">
                {/* Real hrefs, not scroll handlers — these are the crawlable
                    entry points to the mid-funnel pages. A page nothing links to
                    does not get indexed. */}
                <li><button onClick={() => navigate('/features')}>Features</button></li>
                <li><button onClick={() => navigate('/pricing')}>Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')}>FAQ</button></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-col-title">Company</div>
              <ul className="lp-footer-links">
                <li><button onClick={() => navigate('/blog')}>Blog</button></li>
                <li><a href={landingFeedbackHref}>Contact</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-col-title">Legal</div>
              <ul className="lp-footer-links">
                <li><button onClick={() => navigate('/privacy')}>Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')}>Terms of Service</button></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>&copy; 2026 coparent. All rights reserved.</span>
            <div className="lp-footer-social">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
